import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import CreateInviteModal from './CreateInviteModal';

const BoardSettingsModal = ({ isOpen, onClose, board, token, onUpdate, onDelete }) => {
  const [teams, setTeams] = useState([]);
  const [formData, setFormData] = useState(board || {});
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTeams();
      setFormData(board || {});
    }
  }, [isOpen, board]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data.teams || []);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  if (!isOpen || !board) return null;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${board.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          client: formData.client,
          projectType: formData.projectType,
          color: formData.color,
          teamIds: formData.teamIds
        })
      });
      
      if (response.ok) {
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update board:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${board.name}"? This will delete all groups and tasks.`)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${board.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        onDelete();
        onClose();
      }
    } catch (error) {
      console.error('Failed to delete board:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTeam = (teamId) => {
    const teamIds = formData.teamIds || [];
    const newTeamIds = teamIds.includes(teamId)
      ? teamIds.filter(id => id !== teamId)
      : [...teamIds, teamId];
    setFormData({ ...formData, teamIds: newTeamIds });
  };

  const colors = [
    { name: 'Blue', value: '#0F0F10' },
    { name: 'Green', value: '#1DBF73' },
    { name: 'Purple', value: '#9B6BFF' },
    { name: 'Orange', value: '#F4A340' },
    { name: 'Red', value: '#E24B5B' },
    { name: 'Teal', value: '#66ccff' },
  ];

  return (
    <>
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: '600px' }}>
          <div className="modal-header">
            <h2 className="modal-title">Board Settings</h2>
            <button className="modal-close" onClick={onClose}>
              <FiX size={20} />
            </button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Board Name</label>
              <input
                type="text"
                className="input"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="board-settings-name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="input"
                rows="2"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Client</label>
              <input
                type="text"
                className="input"
                value={formData.client || ''}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Board Color</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {colors.map((color) => (
                  <div
                    key={color.value}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: color.value,
                      cursor: 'pointer',
                      border: formData.color === color.value ? '3px solid #323338' : '2px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                    title={color.name}
                  ></div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Team Access</label>
              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: '4px', padding: '8px' }}>
                {teams.map(team => (
                  <label key={team.id} style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={(formData.teamIds || []).includes(team.id)}
                      onChange={() => toggleTeam(team.id)}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{team.name}</span>
                  </label>
                ))}
              </div>
              <p className="form-helper">Select which teams can access this board</p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-danger" onClick={handleDelete} disabled={loading}>
              Delete Board
            </button>
            <div style={{ flex: 1 }}></div>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowInviteModal(true);
                onClose();
              }}
              disabled={loading}
            >
              Share with Client
            </button>
            <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
      
      <CreateInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onCreate={() => setShowInviteModal(false)}
        token={token}
      />
    </>
  );
};

export default BoardSettingsModal;